import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Flex, Group, Loader, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { UI_COLORS } from 'constants/colors';
import { projectsRoutes } from 'constants/routes';

import { usePageTitle } from 'util/hooks/usePageTitle';
import { projectsQueryKeys } from 'util/queryKeys';
import showToast from 'actions/toastActions';
import { createSamplingPlan } from 'store/projects/requests';
import { CreateSamplingPlanPayloadV2Type, ProjectType, RegistryConfig } from 'store/projects/types';
import { Header } from 'common';
import FullHeightLayoutFooter from 'common/FullHeightLayoutFooter';
import FullHeightLayoutWrap from 'common/FullHeightLayoutWrap';

import { useProjectRouteParams } from '../hooks';

import CreateConfirmationModal from './Form/CreateConfirmationModal';
import DepthQuantifyDetails from './Form/DepthQuantifyDetails';
import ExclusionLayerDetails from './Form/ExclusionLayerDetails';
import LandDetails from './Form/LandDetails';
import MeterCoreSampleDetails from './Form/MeterCoreSampleDetails';
import PhotoDetails from './Form/PhotoDetails';
import { useCarbonPlanValidatorQuery } from './Form/queries';
import RegistryDetails from './Form/RegistryDetails';
import SoilHealthDetails from './Form/SoilHealthDetails';
import StratificationDetails from './Form/StratificationDetails';
import ThirtyCmSampleDetails from './Form/ThirtyCmSampleDetails';
import WetAggregateStabilityDetails from './Form/WetAggregateStabilityDetails';
import CreateSamplePlanPanel from './CreateSamplePlanPanel';
import { useProjectsTranslations } from './hooks';
import { useSamplePlanFormContext } from './samplePlanFormContextV2';
import {
  getMeterCorePayload,
  getSoilHealthPayload,
  getThirtyCmCorePayload,
  getWetAggregateStabilityPayload,
} from './utils';

type Props = {
  samplePlanConfig: RegistryConfig;
  project: ProjectType;
};

const CreateSamplePlanForm = ({ samplePlanConfig, project }: Props) => {
  const { projectId } = useProjectRouteParams();
  const translations = useProjectsTranslations();
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const pageTitle = translations.createSamplingPlan;

  usePageTitle(pageTitle);

  const samplePlanForm = useSamplePlanFormContext();
  const queryClient = useQueryClient();

  const createPlanMutation = useMutation({
    mutationFn: (planPayload: CreateSamplingPlanPayloadV2Type) => {
      return createSamplingPlan(planPayload);
    },
    onSuccess: () => {
      showToast(translations.success, 'success');
      queryClient.invalidateQueries({
        queryKey: projectsQueryKeys.projectSamplingPlans(projectId),
      });
      navigate(projectsRoutes.routes.project(projectId));
    },
  });

  const samplePlanFormValues = samplePlanForm.getValues();

  const createPayload = (): CreateSamplingPlanPayloadV2Type => {
    return {
      field_ids: project?.fields.map((field) => field.id) || [],
      sample_plan: {
        project_id: projectId,
        project_type: samplePlanFormValues.project_type,
        method: samplePlanFormValues.method,
        user_defined_points: [], // TODO: define this type and implement logic
        user_defined_exclusions: [], // TODO: define this type and implement logic
        registry_list: samplePlanFormValues.registry_list,
        exclusion_layers: samplePlanFormValues.exclusion_layers,
        exclusion_thresholds: {
          tree_shrub: samplePlanFormValues.tree_shrub,
          slope: samplePlanFormValues.slope,
        },
        mc_dict: getMeterCorePayload(
          samplePlanFormValues.isMeterCore,
          samplePlanFormValues.mc_dict,
          samplePlanConfig?.meter_core.splits,
        ),
        step_probe_dict: getThirtyCmCorePayload(
          samplePlanFormValues.isThirtyCmCore,
          samplePlanFormValues.step_probe_dict,
        ),
        sh_dict: getSoilHealthPayload(
          samplePlanFormValues.isSoilHealth,
          samplePlanFormValues.sh_dict,
        ),
        was_dict: getWetAggregateStabilityPayload(
          samplePlanFormValues.isWetAggregateStability,
          samplePlanFormValues.was_dict,
        ),
      },
    };
  };

  const handleSubmit = () => {
    createPlanMutation.mutate(createPayload(), { onSettled: close });
  };

  const { isValidatorPending, errorMessage } = useCarbonPlanValidatorQuery(createPayload());

  const getErrorAlert = () => {
    if (isValidatorPending) {
      return <Loader />;
    }
    return errorMessage?.msg ? (
      <Alert variant="light" color={UI_COLORS.error} title={translations.warning} maw="40rem">
        {errorMessage.msg}
      </Alert>
    ) : (
      <div />
    );
  };

  return (
    <>
      <FullHeightLayoutWrap>
        <Header
          title={pageTitle}
          breadcrumbsItems={[
            {
              title: translations.allProjects,
              to: projectsRoutes.routes.base,
            },
            {
              title: `${translations.project}${project?.name ? `: ${project.name}` : ''}`,
              to: projectsRoutes.routes.project(projectId),
            },
            {
              title: translations.createSamplingPlan,
            },
          ]}
        />
        <Flex h="100%" w="100%" style={{ overflowY: 'auto' }}>
          <CreateSamplePlanPanel />
          <Stack pb="xl" px="xl" flex={1} h="100%" style={{ overflowY: 'auto' }} mx="auto">
            <RegistryDetails />
            <LandDetails />
            <DepthQuantifyDetails />
            <MeterCoreSampleDetails />
            <ThirtyCmSampleDetails />
            <SoilHealthDetails />
            <WetAggregateStabilityDetails />
            <PhotoDetails />
            <ExclusionLayerDetails />
            <StratificationDetails />
          </Stack>
        </Flex>
        <FullHeightLayoutFooter>
          <Group justify="space-between" w="100%">
            {getErrorAlert()}
            <Group align="flex-end">
              <Button variant="outline" component={Link} to={projectsRoutes.routes.base}>
                {translations.cancel}
              </Button>
              <Button onClick={open} disabled={!!errorMessage?.msg}>
                {translations.generateSamplingPlan}
              </Button>
            </Group>
          </Group>
        </FullHeightLayoutFooter>
      </FullHeightLayoutWrap>
      <CreateConfirmationModal
        onClose={close}
        opened={opened}
        handleSubmit={handleSubmit}
        isPending={createPlanMutation.isPending}
      />
    </>
  );
};

export default CreateSamplePlanForm;
